<script lang="ts" setup>
import { useResponsive } from "@core/composables"
import { useEmConsoCompareStore } from "@modules/energy-manager/stores/em-conso-compare.store"
import { useAuthStore } from "@core/stores/auth.store"
import { useVModel } from "@vueuse/core"
import moment from "moment"
import { useForm } from "vee-validate"
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import * as yup from "yup"

const { isMobile } = useResponsive()

type Props = {
    visible: boolean
}
const emit = defineEmits(["update:visible"])
const props = defineProps<Props>()

const emConsoCompareStore = useEmConsoCompareStore()
const authStore = useAuthStore()

// ============================================
// Traduction
// ============================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Exporter les données du parc",
            quit: "Quitter",
            question: "Spécifications de l'export",
            period: {
                label: "Période",
                start_date: {
                    label: "Date de début",
                    placeholder: "--/--/----",
                },
                end_date: {
                    label: "Date de fin",
                    placeholder: "--/--/----",
                },
                error: "La date de début doit être antérieure à la date de fin.",
            },
            freq: {
                label: "Fréquence des données",
                daily: "Journalière",
                weekly: "Hebdomadaire",
                monthly: "Mensuelle",
            },
            variables: {
                exceeding_label: "{count} variables sélectionnées",
                label: "Variables à exporter",
                min: "Vous devez sélectionner au moins une variable.",
            },
            checkbox: {
                title: "En cas de trous de données journalières :",
                aggregate:
                    "Agréger des données disponibles de façon hebdomadaire et mensuelle",
                not_aggregate:
                    "Laisser les données hebdomadaires et mensuelles vides",
                tootltip:
                    'L\'onglet "Période" sera par conséquent vide en cas de trous de données sur la période demandée.',
            },
            submit: "Générer l'export",
        },
    },
})

// ============================================
// Gestion de la visibilité de la modale
// ============================================

const visible = useVModel(props, "visible", emit)

// ============================================
// Gestion du formulaire
// ============================================
const getFreqOptions = computed(() => {
    return [
        { label: t("freq.daily"), value: "daily" },
        { label: t("freq.weekly"), value: "weekly" },
        { label: t("freq.monthly"), value: "monthly" },
    ]
})

const getVariablesOptions = computed(() => {
    const compteurElectriqueItems = [
        {
            label: "Energie électrique",
            value: "energie_elec_tout.sum",
        },
        {
            label: "Energie électrique injection",
            value: "energie_elecinjection_tout.sum",
        },
    ]
    if (authStore.getBetaFeatures?.export_index) {
        compteurElectriqueItems.push({
            label: "Index de consommation",
            value: [
                "idxenergie_elec_turpeHCH.last",
                "idxenergie_elec_turpeHPE.last",
                "idxenergie_elec_turpeHPH.last",
                "idxenergie_elec_turpeHCE.last",
                "idxenergie_elec_turpeP.last",
            ] as any,
        })
    }

    return [
        {
            label: "Compteur électrique",
            icon: "fa-light fa-plug",
            iconColor: "#EEC137",
            items: compteurElectriqueItems,
        },
        {
            label: "Compteur de gaz",
            icon: "fa-light fa-fire",
            iconColor: "#EEC137",
            items: [
                {
                    label: "Energie gaz",
                    value: "energie_gaz_tout.sum",
                },
            ],
        },
        {
            label: "Station Météo",
            icon: "pi pi-sun",
            iconColor: "#FA8E42",
            items: [
                {
                    label: "Température extérieure",
                    value: "temperature_air_exterieur.mean",
                },
                {
                    label: "Humidité extérieure",
                    value: "humidite_air_exterieur.mean",
                },
                {
                    label: "DJU",
                    value: "dju_air_exterieur.sum",
                },
            ],
        },
        {
            label: "Capteur",
            icon: "fa-light fa-sensor-cloud",
            iconColor: "#B975F9",
            items: [
                {
                    label: "Température intérieure",
                    value: "temperature_air_interieur.mean",
                },
                {
                    label: "Humidité intérieure",
                    value: "humidite_air_interieur.mean",
                },
            ],
        },
    ]
})

const schema = yup.object({
    variables: yup.array().min(1, t("variables.min")).required(),
    freq: yup.string().required().default("daily"),
    aggregate: yup.boolean().required().default(true),
    date_from: yup
        .date()
        .required()
        .test(
            "start-before-end",
            "La date de début doit être antérieure à la date de fin.",
            function (value) {
                const { date_to } = this.parent
                if (!value || !date_to) return true

                if (!value || !date_to) return false
                return value.getTime() < date_to.getTime()
            }
        ),

    date_to: yup
        .date()
        .required()
        .test(
            "end-after-start",
            "La date de fin doit être postérieure à la date de début.",
            function (value) {
                const { date_from } = this.parent
                if (!value || !date_from) return true

                if (!date_from || !value) return false
                return value.getTime() > date_from.getTime()
            }
        )
        .test(
            "end-not-after-today",
            "La date de fin ne peut pas dépasser la date d'aujourd'hui.",
            function (value) {
                if (!value) return true

                const today = new Date()

                today.setHours(0, 0, 0, 0)

                value.setHours(0, 0, 0, 0)

                return value.getTime() <= today.getTime()
            }
        ),
})

const { defineField, handleSubmit, errors } = useForm({
    validationSchema: schema,
    initialValues: {
        freq: "daily",
        aggregate: true,
        date_from: moment().subtract(3, "month").set("h", 0).toDate(),
        date_to: moment().set("h", 0).subtract(1, "day").toDate(),
        variables: getVariablesOptions.value
            .map((group) => group.items.map((item) => item.value))
            .flat(),
    } as {
        variables: string[]
        date_from: Date
        date_to: Date
        freq: string
        aggregate: boolean
    },
})

const [variables] = defineField("variables")
const [dateFrom] = defineField("date_from")
const [dateTo] = defineField("date_to")
const [freq] = defineField("freq")
const [aggregate] = defineField("aggregate")

const isFecthingTask = ref(false)

/**
 * Quand l'utilisateur clique sur exporter.
 * Appel l'export et ferme la modale esuite
 */
const onSubmit = handleSubmit((values) => {
    isFecthingTask.value = true
    // Lance la tâche de l'export
    emConsoCompareStore.exportAllData.fetch({
        freq: values.freq,
        aggregate: values.aggregate,
        date_from: moment(dateFrom.value).format("YYYY-MM-DD"),
        date_to: moment(dateTo.value).format("YYYY-MM-DD"),
        variables: values.variables.flatMap((v) => v),
        callback: () => {
            visible.value = false
            isFecthingTask.value = false
        },
    })
})

watch(
    () => emConsoCompareStore.exportAllData.isFetching,
    (value) => {
        if (!value) isFecthingTask.value = value
    }
)
</script>

<template>
    <BaseDialog
        v-model:visible="visible"
        :position="isMobile ? 'center' : 'top'"
        modal
        class="dashboard-modal-export"
        :pt="{
            footer: 'dashboard-model-select-building-footer',
        }"
        :style="{ maxWidth: '40rem', width: '100%' }"
        :breakpoints="{ '1199px': '90vw' }"
    >
        <template #header>
            <h2 class="text-xl font-bold dashboard-modal-export__title">
                {{ t("title") }}
            </h2>
        </template>
        <div class="dashboard-modal-export__content">
            <!-- Choix de la période lorsque le type est selectionné -->
            <div class="dashboard-modal-export__content">
                <h4 class="font-bold">{{ t("period.label") }}</h4>
                <div class="dashboard-modal-export__dates-wrapper">
                    <!-- Date de début -->
                    <BaseInputWrapper
                        input-id="date_from"
                        :label="t('period.start_date.label')"
                        :error="errors.date_from"
                    >
                        <BaseDatePicker
                            input-id="date_from"
                            v-model="dateFrom"
                            date-format="dd/mm/yy"
                            v-mask="'99/99/9999'"
                            :invalid="!!errors.date_from"
                            :max-date="moment().subtract(1, 'day').toDate()"
                        ></BaseDatePicker>
                    </BaseInputWrapper>
                    <!-- Date de fin -->
                    <BaseInputWrapper
                        input-id="date_to"
                        :label="t('period.end_date.label')"
                        :error="errors.date_to"
                    >
                        <BaseDatePicker
                            input-id="date_to"
                            v-model="dateTo"
                            date-format="dd/mm/yy"
                            v-mask="'99/99/9999'"
                            :invalid="!!errors.date_to"
                            :max-date="moment().subtract(1, 'day').toDate()"
                        ></BaseDatePicker>
                    </BaseInputWrapper>
                </div>
            </div>
            <!-- Choix de la fréquence (cas pour les rapports PDF) -->
            <BaseInputWrapper
                input-id="freq"
                :label="t('freq.label')"
                :error="errors.freq"
            >
                <BaseSelect
                    input-id="freq"
                    :options="getFreqOptions"
                    v-model="freq"
                    :invalid="!!errors.freq"
                    option-value="value"
                    option-label="label"
                />
            </BaseInputWrapper>
            <!-- Choix des variables (cas pour les export Excel) -->
            <BaseInputWrapper
                input-id="variables"
                :label="t('variables.label')"
                :error="errors.variables"
            >
                <BaseMultiSelect
                    v-model="variables"
                    input-id="variables"
                    :options="getVariablesOptions"
                    option-label="label"
                    option-value="value"
                    option-group-children="items"
                    option-group-label="label"
                    :max-selected-labels="3"
                    :selected-items-label="
                        t('variables.exceeding_label', {
                            count: variables.length,
                        })
                    "
                    filter
                    :invalid="!!errors.variables"
                >
                    <template #optiongroup="slotProps">
                        <div class="dashboard-modal-export__option-group-label">
                            <BaseIcon
                                v-if="slotProps.option.icon"
                                :icon="slotProps.option.icon"
                                size="lg"
                                :color="slotProps.option.iconColor"
                            ></BaseIcon>
                            <p>{{ slotProps.option.label }}</p>
                        </div>
                    </template>
                </BaseMultiSelect>
            </BaseInputWrapper>
            <!-- Choix de la gestion des trous de données -->
            <div class="dashboard-modal-export__content">
                <h4 class="font-bold">{{ t("checkbox.title") }}</h4>
                <div class="flex items-center gap-2">
                    <BaseRadioButton
                        v-model="aggregate"
                        input-id="aggregate"
                        :value="true"
                        name="export-all-data-check"
                    />
                    <label for="aggregate">{{ t("checkbox.aggregate") }}</label>
                </div>
                <div class="flex items-center gap-2">
                    <BaseRadioButton
                        v-model="aggregate"
                        input-id="not_aggregate"
                        :value="false"
                        name="export-all-data-check"
                    />
                    <label for="not_aggregate"
                        >{{ t("checkbox.not_aggregate")
                        }}<i
                            class="ml-2 pi pi-info-circle"
                            v-tooltip.left="{
                                value: t('checkbox.tootltip'),
                                pt: {
                                    text: 'text-sm',
                                },
                            }"
                        ></i
                    ></label>
                </div>
            </div>
        </div>
        <template #footer>
            <div class="flex flex-col gap-4 w-full">
                <div class="flex justify-between w-full">
                    <BaseButton
                        :label="t('quit')"
                        text
                        @click="visible = false"
                    ></BaseButton>
                    <BaseButton
                        :label="t('submit')"
                        @click="onSubmit"
                        :loading="isFecthingTask"
                    ></BaseButton>
                </div>
                <BaseAppMessage
                    v-if="emConsoCompareStore.exportAllData.hasError"
                    severity="error"
                >
                    {{ emConsoCompareStore.exportAllData.error }}
                </BaseAppMessage>
            </div>
        </template>
    </BaseDialog>
</template>

<style scoped>
@import "@styles/breakpoints.css";

.dashboard-modal-export {
    .dashboard-modal-export__title {
        text-align: center;
        width: 100%;
    }
    .dashboard-modal-export__content {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }

    .dashboard-modal-export__dates-wrapper {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        gap: 1rem;
        width: 100%;
    }
}
.dashboard-modal-export__option-group-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}
</style>

<style>
.dashboard-model-select-building-footer {
    justify-content: space-between !important;
}
</style>
